import { Injectable, computed, inject } from '@angular/core';

import { UserDetailsStorageService } from '@app/security/data-access/user/user-details-storage.service';

export enum UserRole {
  SuperAdmin = 'ROLE_SUPER_ADMIN',
  FacilityAdmin = 'ROLE_FACILITY_ADMIN',
  Therapist = 'ROLE_FACILITY_EMPLOYEE',
}

export const UserRoleLabels: { [role in UserRole]: string } = {
  [UserRole.SuperAdmin]: 'Super-admin',
  [UserRole.FacilityAdmin]: 'Administrator placówki',
  [UserRole.Therapist]: 'Terapeuta',
};

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  private userDetailsStorage = inject(UserDetailsStorageService);

  public readonly roles = computed<UserRole[]>(() => {
    const user = this.userDetailsStorage.user();
    if (!user) return [];
    return extractRoles(user.roles);
  });

  public readonly primaryRole = computed<UserRole | null>(() => this.roles()[0] ?? null);

  hasRole(role: UserRole): boolean {
    return this.roles().includes(role);
  }

  hasAnyRole(...roles: UserRole[]): boolean {
    return this.roles().some((r) => roles.includes(r));
  }
}

/**
 * `MeViewModel.roles` w CAPD API to `oneOf[array, object]` — normalizujemy do tablicy `UserRole`.
 */
function extractRoles(raw: unknown): UserRole[] {
  const known = Object.values(UserRole) as string[];

  if (Array.isArray(raw)) {
    return raw.filter((r): r is string => typeof r === 'string' && known.includes(r)) as UserRole[];
  }

  if (raw && typeof raw === 'object') {
    return Object.keys(raw).filter((key) => known.includes(key)) as UserRole[];
  }

  return [];
}
